import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Anchor,
  Badge,
  Flex,
  Icon,
  Menu,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
} from '@percent/lemonade'
import type { SectionProps } from '@percent/lemonade'
import type { GroupedDomainAssignee } from '../../../types/permissions.types'
import { getAssigneeDisplayName, getAssigneeTypeLabel } from '../utils'
import type { DomainAssigneesTableProps } from './DomainAssigneesTable.types'

export function DomainAssigneesTable({
  assignees,
  domainRoles,
  isLoading,
  nextPage,
  previousPage,
  actionsAndFilters,
  getAssigneeHref,
  canSetRoles,
  canManageAssignee = () => true,
  onEditRoles,
  onRemove,
}: DomainAssigneesTableProps) {
  const { t } = useTranslation()

  const roleNames = useMemo(
    () => new Map(domainRoles.map((role) => [role.id, role.name])),
    [domainRoles]
  )

  const columns = [
    {
      id: 'name',
      content: t('rolesAndPermissions.table.name'),
      props: { width: '40%' },
    },
    {
      id: 'type',
      content: t('rolesAndPermissions.table.type'),
      props: { width: '15%' },
    },
    {
      id: 'roles',
      content: t('rolesAndPermissions.table.roles'),
      props: { width: '35%' },
    },
    ...(canSetRoles
      ? [{ id: 'actions', content: '', props: { width: '10%' } }]
      : []),
  ]

  const getMenuSections = (assignee: GroupedDomainAssignee): SectionProps[] => {
    const isDisabled = !canManageAssignee(assignee)
    const disabledTooltipInfo = isDisabled
      ? t('rolesAndPermissions.menu.ownAssigneeDisabled')
      : undefined

    return [
      {
        items: [
          {
            key: 'editRoles',
            label: t('rolesAndPermissions.menu.editRoles'),
            onClick: () => onEditRoles(assignee),
            shouldBeDisabled: isDisabled,
            disabledTooltipInfo,
            dataTestId: `assignee-edit-roles-${assignee.id}`,
          },
          {
            key: 'remove',
            label: t('rolesAndPermissions.menu.remove'),
            onClick: () => onRemove(assignee),
            shouldBeDisabled: isDisabled,
            disabledTooltipInfo,
            dataTestId: `assignee-remove-${assignee.id}`,
          },
        ],
      },
    ]
  }

  const renderName = (assignee: GroupedDomainAssignee) => {
    const name = getAssigneeDisplayName(assignee)
    const href = getAssigneeHref?.(assignee)

    return href ? (
      <Anchor
        component={Link}
        to={href}
        data-testid={`assignee-name-${assignee.id}`}
      >
        {name}
      </Anchor>
    ) : (
      <span data-testid={`assignee-name-${assignee.id}`}>{name}</span>
    )
  }

  return (
    <TableProvider
      columns={columns}
      // The assignees endpoint is cursor-paginated and returns no total, so count the rows on
      // this page: a zero total renders the empty state and hides pagination.
      totalResults={assignees.length}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={t('rolesAndPermissions.emptyTable.title')}
      emptyTableDescription={t('rolesAndPermissions.emptyTable.description')}
    >
      {actionsAndFilters && (
        <TableSearchAndFilters>
          <Flex direction="row" justify="flex-end" w="100%">
            {actionsAndFilters}
          </Flex>
        </TableSearchAndFilters>
      )}
      <Table>
        <TableBody>
          {assignees.map((assignee) => (
            <TableRow
              key={`${assignee.type}-${assignee.id}`}
              data-testid={`assignee-row-${assignee.id}`}
            >
              <TableCell>{renderName(assignee)}</TableCell>
              <TableCell>{getAssigneeTypeLabel(assignee.type, t)}</TableCell>
              <TableCell>
                <Flex direction="row" gap="xs" wrap="wrap">
                  {assignee.roles.map((roleId) => (
                    <Badge key={roleId} variant="default">
                      {roleNames.get(roleId) ?? roleId}
                    </Badge>
                  ))}
                </Flex>
              </TableCell>
              {canSetRoles && (
                <TableCell>
                  <Menu
                    sections={getMenuSections(assignee)}
                    customTriggerComponent={
                      <span
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        aria-label={t('rolesAndPermissions.menu.actions', {
                          name: getAssigneeDisplayName(assignee),
                        })}
                        data-testid={`assignee-menu-${assignee.id}`}
                      >
                        <Icon name="menu-vertical" size={5} />
                      </span>
                    }
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination previousPage={previousPage} nextPage={nextPage} />
    </TableProvider>
  )
}
